import { Link } from "react-router";

import { Badge, CountBadge } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";

import type { NavItem } from "./navItems";

interface SidebarItemProps {
  item: NavItem;
  active: boolean;
  /** Sin ver, si el ítem lleva contador. */
  count: number;
  compact: boolean;
  onNavigate?: (() => void) | undefined;
}

const BASE =
  "rounded-chip flex min-h-10 w-full items-center gap-3 px-3 text-sm transition-colors";

export function SidebarItem({ item, active, count, compact, onNavigate }: SidebarItemProps) {
  const Icon = item.icon;
  const content = (
    <>
      <span className="relative shrink-0">
        <Icon aria-hidden strokeWidth={1.75} className="size-[17px]" />
        {compact && count > 0 && (
          <span
            aria-hidden
            className="bg-highlight absolute -top-0.5 -right-0.5 size-2 rounded-full"
          />
        )}
      </span>
      {!compact && (
        <>
          <span className="flex-1 truncate text-left">{item.label}</span>
          {item.status === "beta" && (
            <Badge tone="highlight" size="sm">
              Beta
            </Badge>
          )}
          {item.status === "locked" && (
            <span className="bg-rail-hover text-rail-muted rounded-tag px-1.5 py-0.5 text-[10px] font-medium">
              En desarrollo
            </span>
          )}
          <CountBadge count={count} label={`${count} sin ver`} />
        </>
      )}
      {compact && <span className="sr-only">{item.label}</span>}
    </>
  );

  // El backend no le da destino a lo que está en construcción: no hay enlace roto posible.
  if (!item.href) {
    return (
      <button
        type="button"
        disabled
        title={item.description}
        className={cn(BASE, "text-rail-muted cursor-not-allowed opacity-60")}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={item.href}
      title={compact ? item.label : item.description}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
      className={cn(
        BASE,
        compact && "justify-center px-0",
        active
          ? "bg-rail-active text-rail-active-foreground font-semibold"
          : "text-rail-item hover:bg-rail-hover hover:text-rail-foreground",
      )}
    >
      {content}
    </Link>
  );
}
